import { inject, Injectable } from '@angular/core';
import { WindowService, WindowServiceInterface } from '@campus/browser';
import {
  EduContent,
  EduContentInterface,
  EduContentTypeEnum,
  LearnosityApi,
  LearnosityAssessmentApiService,
  LearnosityItemsConfigInterface,
  LearnosityItemsState,
  LearnosityItemsType,
  RolesEnum,
  ScormCmiMode,
} from '@campus/dal';
import {
  BrowseExerciseConfigurationInterface,
  CustomExerciseServiceInterface,
  ExerciseConfiguration,
  GradingExerciseConfigurationInterface,
  NormalExerciseConfigurationInterface,
  ReviewExerciseConfigurationInterface,
} from '@campus/exercise';

import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { LearnosityDialogComponentType, RESULT_UPDATER_TOKEN } from '../interfaces';
import { LearnosityOverlayService } from './learnosity-overlay.service';

export enum LearnosityStateEnum {
  INITIAL = 'initial', // automatically applied by Learnosity
  PREVIEW = 'preview',
  RESUME = 'resume', // automatically applied by Learnosity
  REVIEW = 'review',
}

@Injectable({ providedIn: 'root' })
export class LearnosityOpenerService implements CustomExerciseServiceInterface {
  private learnosityAssessmentService = inject(LearnosityAssessmentApiService);
  private learnosityApi = inject(LearnosityApi);

  private overlayService = inject(LearnosityOverlayService);
  private windowService: WindowServiceInterface = inject(WindowService);
  private resultUpdater = inject(RESULT_UPDATER_TOKEN, { optional: true });

  public isContentCompatible(content: EduContentInterface): boolean {
    if (content.type === EduContentTypeEnum.DRILL) {
      return true;
    }
    const eduContent = content instanceof EduContent ? content : EduContent.toEduContent(content);
    return eduContent.isLearnosity;
  }

  public shouldGetResult(data: ExerciseConfiguration): boolean {
    return false; // result is created when the exercise is started
  }

  public loadExercise(data: ExerciseConfiguration) {
    const { mode } = data;

    switch (mode) {
      case ScormCmiMode.CMI_MODE_NORMAL:
        // new result
        // modes: without feedback, with feedback, resume (handled by learnosity)
        // save scores
        return this.start(data);
      case ScormCmiMode.CMI_MODE_BROWSE: // intended for previewing exercises, however LUDO uses custom 'CMI_MODE_PREVIEW' instead
        // no result,
        // don't save scores
        return this.preview(data);
      case ScormCmiMode.CMI_MODE_REVIEW:
        // review an existing result
        // teacher: through reports
        // student: through assessment
        // don't save scores
        return this.review(data);
      case ScormCmiMode.CMI_MODE_GRADING:
        // review an existing result
        // teacher: through reports
        // student: through assessment
        // don't save scores
        return this.grade(data);
      default:
        console.warn(`Mode <<${mode}>> is not supported by the LearnosityOpenerService`);
    }
  }

  private openExercise(
    config: LearnosityItemsConfigInterface,
    component: LearnosityDialogComponentType,
    eduContent?: EduContentInterface
  ) {
    if (!config) {
      console.warn('Cannot open learnosity exercise without config');
      return;
    }

    if (this.overlayService.isOpen()) {
      console.warn('A learnosity exercise is already open. Close the existing dialog first.');
      return;
    }

    const { result } = config;
    if (result) this.resultUpdater?.(result);

    this.overlayService.open({ config, component, eduContent });
    this.notifyWindowServiceOnClose();
  }

  private notifyWindowServiceOnClose(): void {
    this.overlayService.closed$.pipe(take(1)).subscribe(() => {
      this.windowService.notifyDialogClosed();
    });
  }

  private preview(config: BrowseExerciseConfigurationInterface) {
    const { eduContent } = config;
    const config$ = this.getPreviewConfig(config);

    config$.pipe(take(1)).subscribe((config) => {
      this.openExercise(config, 'assessment', eduContent);
    });
  }

  private grade(config: GradingExerciseConfigurationInterface) {
    const { role } = config;

    if (role !== RolesEnum.Teacher) return;
    this.gradeAsTeacher(config);
  }

  private review(config: ReviewExerciseConfigurationInterface) {
    const { role } = config;

    switch (role) {
      case RolesEnum.Student:
        this.reviewAsStudent(config);
        break;
      case RolesEnum.Teacher:
        this.reviewAsTeacher(config);
        break;

      default:
        console.warn(`Review as role <<${role}>> is not supported by the LearnosityOpenerService`);
        break;
    }
  }

  private reviewAsStudent(config: ReviewExerciseConfigurationInterface) {
    const { eduContent, result } = config;

    const eduContentWithQuestions = {
      ...eduContent,
      questions: eduContent.publishedEduContentMetadata?.questions || eduContent.questions,
    };

    const reviewInGradingMode = eduContentWithQuestions.questions?.some((q) => q.needsGrading);
    if (reviewInGradingMode) return this.reviewInGradingAsStudent(config);

    const config$ = this.getConfig(config);

    config$.pipe(take(1)).subscribe((config) => {
      this.openExercise({ ...config, result }, 'assessment', eduContentWithQuestions);
    });
  }

  private gradeAsTeacher(config: GradingExerciseConfigurationInterface) {
    const { eduContent, result } = config;

    const config$ = this.getGradingConfig(result.personId);
    config$.pipe(take(1)).subscribe((config) => {
      this.openExercise({ ...config, result }, 'grading', eduContent);
    });
  }

  private reviewInGradingAsStudent(config: ReviewExerciseConfigurationInterface) {
    const { eduContent, result } = config;

    const config$ = this.getGradingConfig();
    config$.pipe(take(1)).subscribe((config) => {
      this.openExercise(
        {
          ...config,
          result: {
            ...result,
            eduContent,
          },
        },
        'grading',
        eduContent
      );
    });
  }

  private reviewAsTeacher(config: ReviewExerciseConfigurationInterface) {
    const { eduContent } = config;

    const config$ = this.getReportsConfig(config);

    config$.pipe(take(1)).subscribe((config) => {
      this.openExercise(config, 'reporting', eduContent);
    });
  }

  private start(data: NormalExerciseConfigurationInterface) {
    const { playerOptions, eduContent } = data;

    const config$ = playerOptions?.localSession ? this.getLocalConfig(data) : this.getConfig(data);
    config$.pipe(take(1)).subscribe((config) => {
      this.openExercise(config, 'assessment', eduContent);
    });
  }

  private getConfig(
    configuration: NormalExerciseConfigurationInterface | ReviewExerciseConfigurationInterface
  ): Observable<LearnosityItemsConfigInterface> {
    const { eduContent, result } = configuration;
    let itemReferences = undefined;

    if (eduContent.type === EduContentTypeEnum.DRILL && eduContent.publishedEduContentMetadata?.data) {
      itemReferences = JSON.parse(eduContent.publishedEduContentMetadata.data)?.learnosityItemReferences;
    }
    const settings = this.getSettingsFromConfig(configuration);

    let context = {};
    if ('context' in configuration) {
      context = configuration.context;
    }

    const learnosityConfig$ = this.learnosityAssessmentService.getLearnosityItemsConfig(
      eduContent.id,
      EduContent.toEduContent(eduContent).learnosityReference,
      result?.learnositySessionId,
      { settings, context, itemReferences }
    );

    return learnosityConfig$;
  }

  private getLocalConfig(
    configuration: NormalExerciseConfigurationInterface
  ): Observable<LearnosityItemsConfigInterface> {
    const { eduContent } = configuration;
    const settings = this.getSettingsFromConfig(configuration);
    let itemReferences = undefined;
    if (eduContent.type === EduContentTypeEnum.DRILL && eduContent.publishedEduContentMetadata?.data) {
      itemReferences = JSON.parse(eduContent.publishedEduContentMetadata.data)?.learnosityItemReferences;
    }

    const learnosityConfig$ = this.learnosityAssessmentService.getLearnosityLocalItemsConfig(
      eduContent.id,
      EduContent.toEduContent(eduContent).learnosityReference,
      { settings, itemReferences }
    );

    return learnosityConfig$;
  }

  private getPreviewConfig(config: BrowseExerciseConfigurationInterface): Observable<LearnosityItemsConfigInterface> {
    const { eduContent } = config;
    const learnosityReference = EduContent.toEduContent(eduContent).learnosityReference;
    const learnosityConfig$ = this.learnosityApi.previewActivity(learnosityReference);

    return learnosityConfig$;
  }

  private getReportsConfig(config: ReviewExerciseConfigurationInterface): Observable<LearnosityItemsConfigInterface> {
    const { result } = config;
    const { id } = result;
    const learnosityConfig$ = this.learnosityApi.buildReportsRequest(id);
    return learnosityConfig$;
  }

  private getGradingConfig(studentId?: number): Observable<LearnosityItemsConfigInterface> {
    const learnosityConfig$ = this.learnosityAssessmentService.buildGradingRequest(studentId);
    return learnosityConfig$;
  }

  private getSettingsFromConfig(config: ExerciseConfiguration): {
    state: LearnosityItemsState;
    type?: LearnosityItemsType;
  } {
    // @ts-ignore
    const { mode, playerOptions } = config;

    const state = this.mapScormModeToLearnosityState(mode);
    const type: LearnosityItemsType = playerOptions?.localSession ? 'local_practice' : undefined;

    return { state, ...(type ? { type } : {}) };
  }

  /**
   * Maps the cmi mode to a state supported by the Learnosity API
   *
   * @private
   * @param {string} mode
   * @return {*}  {string}
   * @memberof LearnosityOpenerService
   */
  private mapScormModeToLearnosityState(mode: ScormCmiMode): LearnosityItemsState {
    // https://help.learnosity.com/hc/en-us/articles/360000755438-Switching-Between-Testing-and-Reviewing-With-States
    switch (mode) {
      case ScormCmiMode.CMI_MODE_NORMAL:
        return null; // applied automatically by Learnosity
      case ScormCmiMode.CMI_MODE_BROWSE:
        return LearnosityStateEnum.PREVIEW;
      default:
        // scorm mode is the same as learnosity state
        return mode;
    }
  }
}
